/**
 * AddGatewayDialog — the **Add-a-Thing** wizard (P6.3).
 *
 * Replaces the old 3-field "claim a gateway" form. A Thing is an instance
 * of one of our gateway *products*; the field drivers a product may run
 * (`drivers`: Modbus TCP / RTU, OPC UA, S7, EtherNet/IP, MELSEC) are a product
 * attribute, and the platform transport is always MQTT. Tenants onboard a
 * Thing themselves in two steps:
 *
 *   Step 1 — pick a gateway product (cards from GET /v1/things/products),
 *            name the Thing, and flip a per-Thing Simulate toggle.
 *   Step 2 — author repeatable tag rows (name, data_type, unit, poll_sec,
 *            optional address / min / max / per-tag simulate override); or,
 *            for a product that polls devices (any product with drivers:
 *            a VSync, a Modbus gateway, the Multi-protocol Gateway), create
 *            the gateway and add its devices inline (GatewayInstruments).
 *
 * Submit → POST /v1/things with the pinned contract; the parent refetches
 * the things list. The component keeps its original name + {open,onClose}
 * props so the Things page wiring is unchanged (plus an optional onCreated).
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Cable,
  ClipboardPen,
  Cpu,
  Gauge,
  Loader2,
  Network,
  Radio,
  ScanLine,
  Waves,
} from "lucide-react";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Badge } from "@/lib/ui/badge";
import { cn } from "@/lib/ui/cn";
import { useToast } from "@/lib/ui/toast";
import { WizardFooter, WizardLayout } from "@/lib/ui/wizard-layout";
import {
  TagRowsEditor,
  blankRow,
  namedRowsOf,
  toTagInputs,
  type TagRow,
} from "@/components/iot/ThingTagRows";
import {
  things,
  listOrgTags,
  isLiveApi,
  productDrivers,
  type ThingProduct,
} from "@/lib/api";
import { GatewayInstruments } from "@/components/iot/GatewayInstruments";
import { connectToGatewayHref } from "@/components/connect/links";
import { driverLabel, productPollsDevices } from "@/lib/iot/drivers";

/**
 * Lucide icon picked from the product's `icon` hint (best-effort), by the
 * catalogue's icon hints and product ids. The Multi-protocol Gateway
 * (`gatewaypro`) uses the "network" hint.
 */
const ICON_BY_HINT: Record<string, typeof Cpu> = {
  modbus: Cpu,
  "modbus-tcp": Network,
  "modbus-rtu": Cable,
  network: Network,
  gatewaypro: Network,
  cable: Cable,
  cpu: Cpu,
  radio: Radio,
  gauge: Gauge,
  mqtt: Waves,
  "generic-mqtt": Waves,
  waves: Waves,
  "scan-line": ScanLine,
  "clipboard-pen": ClipboardPen,
};

/** "a VSync" for the VSync products, else "the gateway", for the copy around adding devices. */
function gatewayNoun(p: ThingProduct | null): string {
  return p && /vsync/i.test(`${p.id} ${p.label}`) ? "VSync" : "gateway";
}

function productIcon(p: ThingProduct): typeof Cpu {
  return ICON_BY_HINT[p.icon] ?? ICON_BY_HINT[p.id] ?? Cpu;
}

export interface AddGatewayDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a Thing is created so the parent can refetch. */
  onCreated?: () => void;
}

export function AddGatewayDialog({ open, onClose, onCreated }: AddGatewayDialogProps) {
  const toast = useToast();

  const [step, setStep] = useState<1 | 2>(1);
  const [products, setProducts] = useState<ThingProduct[]>([]);
  const [productsError, setProductsError] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [simulate, setSimulate] = useState(true);
  const [rows, setRows] = useState<TagRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // For a VSync (Modbus) gateway, Step 2 has no tag editor: we create the
  // gateway first (no tags of its own), then swap in-place to the instrument
  // panel keyed on the new gateway id. `createdGateway` marks that hand-off.
  const [createdGateway, setCreatedGateway] = useState<{ id: string; name: string } | null>(
    null,
  );
  // Existing tag + unit names in the org → autocomplete suggestions.
  const [tagNameOptions, setTagNameOptions] = useState<string[]>([]);
  const [unitOptions, setUnitOptions] = useState<string[]>([]);

  const product = useMemo(
    () => products.find((p) => p.id === productId) ?? null,
    [products, productId],
  );
  const pollDefault = product?.default_poll_sec ?? 5;
  // A product with drivers polls field devices: it hosts instruments (child
  // sensor Things), so it publishes nothing of its own. That flips Step 2 from
  // "author tags" to "create gateway → add instruments". (An older server with
  // no `drivers` falls back to a Modbus field protocol, the previous rule.)
  const isModbus = productPollsDevices(product);
  const noun = gatewayNoun(product);

  // Load the product catalogue when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    things
      .products()
      .then((r) => {
        if (alive) setProducts(r.products);
      })
      .catch(() => {
        if (alive) setProductsError(true);
      });
    // Autocomplete sources (live mode only — stub has no tag catalogue here).
    if (isLiveApi) {
      listOrgTags()
        .then((r) => {
          if (!alive) return;
          setTagNameOptions(r.items.map((t) => t.name).filter(Boolean));
          setUnitOptions(
            r.items.map((t) => t.unit).filter((u): u is string => !!u),
          );
        })
        .catch(() => {});
    }
    return () => {
      alive = false;
    };
  }, [open]);

  // Picking a product also sets the Simulate default: a real VSync/Modbus
  // gateway is physical hardware, so default Simulate OFF (else it would be
  // marked simulated and swept by sim-cleanup); simulated products default ON.
  // The user can still flip the toggle in Step 1 afterwards.
  const pickProduct = (id: string | null) => {
    setProductId(id);
    const p = id ? products.find((x) => x.id === id) : null;
    setSimulate(!productPollsDevices(p));
  };

  const reset = () => {
    setStep(1);
    setProductId(null);
    setName("");
    setSimulate(true);
    setRows([]);
    setSubmitting(false);
    setCreatedGateway(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canAdvance = !!productId && name.trim().length > 1;

  const goToStep2 = () => {
    if (!canAdvance) return;
    // Seed one empty tag row (non-VSync only — a VSync authors no tags of its
    // own; its data comes from instruments added after the gateway is created).
    if (!isModbus && rows.length === 0) setRows([blankRow(pollDefault)]);
    setStep(2);
  };

  const addRow = () => setRows((prev) => [...prev, blankRow(pollDefault)]);
  const removeRow = (key: string) =>
    setRows((prev) => prev.filter((r) => r.key !== key));
  const patchRow = (key: string, patch: Partial<TagRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const namedRows = namedRowsOf(rows);
  const canSubmit = !!productId && name.trim().length > 1 && !submitting;

  const submit = () => {
    if (!productId || !canSubmit) return;
    // A VSync gateway carries no tags of its own — its instruments do. Everyone
    // else authors the tag rows from Step 2.
    const tags = isModbus ? [] : toTagInputs(rows);

    setSubmitting(true);
    things
      .create({ product_id: productId, name: name.trim(), simulate, tags })
      .then((created) => {
        onCreated?.();
        if (isModbus) {
          // Don't close — advance in-place to adding this gateway's instruments.
          setCreatedGateway({ id: created.id, name: created.name });
          setSubmitting(false);
          return;
        }
        toast.push(
          "ok",
          `Added "${created.name}" — ${created.tag_count} tag${created.tag_count === 1 ? "" : "s"}`,
        );
        handleClose();
      })
      .catch(() => {
        toast.push("error", "Couldn't add the gateway. Check the fields and try again.");
        setSubmitting(false);
      });
  };

  return (
    <Sheet open={open} onClose={handleClose} widthClass="max-w-2xl" ariaLabel="Add a gateway">
      <SheetHeader
        title={
          step === 1
            ? "Add a gateway"
            : isModbus
              ? `Instruments · ${name.trim() || `New ${noun}`}`
              : `Tags · ${name.trim() || "New gateway"}`
        }
        subtitle={
          step === 1
            ? "Pick a gateway product, name it, and choose whether to simulate."
            : isModbus
              ? `Create the ${noun}, then add each instrument with its connection and points.`
              : "Set the readings this gateway sends. They travel over MQTT."
        }
        onClose={handleClose}
      />

      <WizardLayout stepKey={step}>
        <div className="flex flex-1 flex-col gap-4 p-5">
          {/* Step indicator */}
          <ol className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide">
            <StepPip n={1} label="Product" active={step === 1} done={step > 1} />
            <span className="h-px w-6 bg-border" aria-hidden />
            <StepPip
              n={2}
              label={isModbus ? "Instruments" : "Tags"}
              active={step === 2}
              done={false}
            />
          </ol>

          {step === 1 ? (
            <>
              <ConnectSteerNote onGo={handleClose} />
              <Step1
                products={products}
                productsError={productsError}
                productId={productId}
                onPickProduct={pickProduct}
                name={name}
                onName={setName}
                simulate={simulate}
                onSimulate={setSimulate}
              />
            </>
          ) : (
            <div className="flex flex-col gap-3">
              {product && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2 text-[11px] text-on-surface-muted">
                  <span className="font-medium text-on-surface">{product.label}</span>
                  <Badge variant="secondary" className="font-mono text-[9px] uppercase">
                    {product.field_protocol}
                  </Badge>
                  {productDrivers(product).length > 1 && (
                    <span>{productDrivers(product).map((d) => driverLabel(d)).join(" · ")}</span>
                  )}
                  <span>·</span>
                  <span>transport {product.transport}</span>
                  <span>·</span>
                  <span>default {product.default_poll_sec}s poll</span>
                </div>
              )}
              {isModbus ? (
                createdGateway ? (
                  /* Gateway created — add its instruments in-place (register maps
                     from the model-template catalog), no page hop. */
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-success/40 bg-success/5 px-3 py-2 text-[11px] text-on-surface">
                      <span className="min-w-0 flex-1">
                        <span className="font-medium">{createdGateway.name}</span> created. Add its
                        instruments below, or scan the plant network for them.
                      </span>
                      <Link
                        to={connectToGatewayHref(createdGateway.id)}
                        onClick={handleClose}
                        className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                      >
                        Scan with Connect a device
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </div>
                    <GatewayInstruments gatewayId={createdGateway.id} />
                  </div>
                ) : (
                  /* A VSync authors no tags of its own — it hosts instruments. */
                  <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-[11px] text-on-surface">
                    <span className="font-medium">
                      {noun === "VSync" ? "A VSync is a gateway." : `${product?.label ?? "This product"} is a gateway.`}
                    </span>{" "}
                    It publishes no data of its own: create it, then add its instruments on the next
                    screen.
                  </div>
                )
              ) : (
                <TagRowsEditor
                  rows={rows}
                  onAddRow={addRow}
                  onRemoveRow={removeRow}
                  onPatchRow={patchRow}
                  thingSimulate={simulate}
                  tagNameOptions={tagNameOptions}
                  unitOptions={unitOptions}
                />
              )}
            </div>
          )}

          {/* Footer: pinned to the bottom of the sheet; the step scrolls above it. */}
          <WizardFooter className="flex items-center justify-between gap-3 px-5 py-3">
            {step === 1 ? (
              <>
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button size="sm" onClick={goToStep2} disabled={!canAdvance}>
                  Next: {isModbus ? "instruments" : "tags"}
                  <ArrowRight className="ml-1.5 size-4" />
                </Button>
              </>
            ) : isModbus && createdGateway ? (
              /* Gateway made; instruments are added inline above — just finish. */
              <>
                <span className="text-[11px] text-on-surface-muted">
                  Add the instruments above, then finish.
                </span>
                <Button size="sm" onClick={handleClose}>
                  <Check className="mr-1.5 size-4" />
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                  <ArrowLeft className="mr-1.5 size-4" />
                  Back
                </Button>
                <div className="flex items-center gap-3">
                  {!isModbus && (
                    <span className="text-[11px] text-on-surface-muted">
                      {namedRows.length} tag{namedRows.length === 1 ? "" : "s"}
                    </span>
                  )}
                  <Button size="sm" onClick={submit} disabled={!canSubmit} data-tour="add-thing-save">
                    {submitting ? (
                      <Loader2 className="mr-1.5 size-4 animate-spin" />
                    ) : (
                      <Check className="mr-1.5 size-4" />
                    )}
                    {isModbus ? `Create ${noun === "VSync" ? "VSync gateway" : "gateway"}` : "Create gateway"}
                  </Button>
                </div>
              </>
            )}
          </WizardFooter>
        </div>
      </WizardLayout>
    </Sheet>
  );
}

/**
 * Steering note (onboarding-surface cleanup) — the equipment-first Connect flow
 * is the path that lights up modeled equipment (it binds a real PLC's registers
 * to the equipment's existing tags). "Add a Thing" here creates a STANDALONE
 * data source with its own tags, not linked to any modeled equipment, which is
 * the parallel-source confusion the redesign is retiring. Surface the better
 * path at the moment of choice, without removing this one (kept reachable one
 * cycle as a fallback).
 */
function ConnectSteerNote({ onGo }: { onGo: () => void }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-2.5 text-[11px] leading-snug">
      <span className="text-xs font-medium text-on-surface">
        Connecting a real PLC to equipment you have already modeled?
      </span>
      <span className="text-on-surface-muted">
        Use <span className="font-medium text-on-surface">Connect a device</span>: it feeds your
        existing equipment instead of creating a standalone data source.
      </span>
      <Link
        to="/app/connect"
        onClick={onGo}
        className="mt-0.5 inline-flex w-fit items-center gap-1 font-medium text-brand hover:underline"
      >
        Go to Connect a device
        <ArrowRight className="size-3.5" />
      </Link>
    </div>
  );
}

function StepPip({
  n,
  label,
  active,
  done,
}: {
  n: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <li className="flex items-center gap-1.5">
      <span
        className={cn(
          "flex size-5 items-center justify-center rounded-full text-[10px]",
          active
            ? "bg-brand text-brand-foreground"
            : done
              ? "bg-success/20 text-success"
              : "bg-surface-muted text-on-surface-muted",
        )}
      >
        {done ? <Check className="size-3" /> : n}
      </span>
      <span className={cn(active ? "text-on-surface" : "text-on-surface-muted")}>
        {label}
      </span>
    </li>
  );
}

function Step1({
  products,
  productsError,
  productId,
  onPickProduct,
  name,
  onName,
  simulate,
  onSimulate,
}: {
  products: ThingProduct[];
  productsError: boolean;
  productId: string | null;
  onPickProduct: (id: string) => void;
  name: string;
  onName: (v: string) => void;
  simulate: boolean;
  onSimulate: (v: boolean) => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Gateway product
        </span>
        {productsError && products.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-surface-elevated p-3 text-xs text-on-surface-muted">
            Couldn't load the product catalogue. The backend may be offline in
            local dev — try again once it's up.
          </p>
        ) : products.length === 0 ? (
          <div className="flex items-center gap-2 p-3 text-xs text-on-surface-muted">
            <Loader2 className="size-4 animate-spin" /> Loading products…
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-tour="add-thing-product">
            {/* manual-entry-app-gateway is auto-provisioned from an equipment's
                "Manual-entry tags" panel (one coherent path) — not picked here,
                where it would create an orphan gateway with no operator. */}
            {products
              .filter((p) => p.id !== "manual-entry-app-gateway")
              .map((p) => {
              const Icon = productIcon(p);
              const active = productId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onPickProduct(p.id)}
                  className={cn(
                    "flex flex-col gap-1.5 rounded-md border p-3 text-left transition-colors",
                    active
                      ? "border-brand bg-brand-muted/30"
                      : "border-border bg-surface hover:border-brand/60",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="size-4 text-brand" />
                    <span className="flex-1 text-sm font-semibold text-on-surface">
                      {p.label}
                    </span>
                    {active && <Check className="size-4 text-brand" />}
                  </div>
                  <p className="text-[11px] leading-snug text-on-surface-muted">
                    {p.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge variant="secondary" className="font-mono text-[9px] uppercase">
                      {p.field_protocol}
                    </Badge>
                    {productDrivers(p).length > 1 &&
                      productDrivers(p).map((d) => (
                        <Badge key={d} variant="outline" className="text-[9px]">
                          {driverLabel(d)}
                        </Badge>
                      ))}
                    <Badge variant="outline" className="font-mono text-[9px] uppercase">
                      {p.transport}
                    </Badge>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Gateway name
        </span>
        <Input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="Furnace 3 PLC"
          autoFocus
        />
      </label>

      <button
        type="button"
        onClick={() => onSimulate(!simulate)}
        className="flex items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2.5 text-left"
        aria-pressed={simulate}
      >
        <div className="flex flex-col">
          <span className="text-sm font-medium text-on-surface">Simulate this gateway</span>
          <span className="text-[11px] text-on-surface-muted">
            Generate synthetic readings for every tag until real data flows.
          </span>
        </div>
        <Toggle on={simulate} />
      </button>
    </>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        on ? "bg-brand" : "bg-surface-muted",
      )}
    >
      <span
        className={cn(
          "inline-block size-4 transform rounded-full bg-surface shadow transition-transform",
          on ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </span>
  );
}
